import { Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { ActionItem, OrderProgressParticipant } from '@ekhodealer/ekho-common';

import { EmptySection } from '@common/components/EmptySection';

import { ActionItemView } from './ActionItemView';

interface ProgressSectionProps {
	/** The title of the section */
	title: string;
	/** Array of action items to display (ordered by backend) */
	actionItems: ActionItem[];
	/** List of participants with agentId and agentName mappings */
	participants: OrderProgressParticipant[];
}

/**
 * Get the agent name from participants list by matching agentId
 */
const getAgentName = (
	agentId: string | undefined,
	participants: OrderProgressParticipant[],
): string => {
	if (!agentId) {
		return 'Unknown';
	}

	const participant = participants.find((p) => p.agentId === agentId);
	return participant?.agentName || agentId;
};

/**
 * ProgressSection component that displays a section with a title
 * and a list of action items. Action items are displayed in the order
 * provided by the backend without any frontend sorting.
 */
export const ProgressSection = ({ title, actionItems, participants }: ProgressSectionProps) => {
	if (actionItems.length === 0) {
		return (
			<Stack itemsSpacing='16'>
				<Text size='body' weight='semibold'>
					{title}
				</Text>
				<EmptySection message={`No ${title.toLowerCase()} action items`} />
			</Stack>
		);
	}

	return (
		<Stack itemsSpacing='16'>
			<Text size='body' weight='semibold'>
				{title}
			</Text>
			<Stack itemsSpacing='12'>
				{actionItems.map((actionItem) => (
					<ActionItemView
						key={actionItem.id}
						actionItem={actionItem}
						agentName={getAgentName(actionItem.agentId, participants)}
					/>
				))}
			</Stack>
		</Stack>
	);
};
